import {Store, createStore} from 'vuex';
import BaseMapStylesDialogModule, {
  BaseMapStylesState
} from "../.././base_map_dialog/store/modules/BaseMapStylesDialogModule";
import PlanTrailModule, {PlanTrailState} from "./modules/PlanTrailModule";
import WaypointDialogModule, {WaypointDialogState} from "../../waypoint_dialog/store/module/WaypointOptionsDialogModule";
import BaseMapDataPOIsModule, { BaseMapDataPOIsState } from "../../base_map_dialog/store/modules/BaseMapDataPOIsModule";
import BaseMapDataOverlaysModule, { BaseMapDataOverlaysState } from "../../base_map_dialog/store/modules/BaseMapDataOverlaysModule";
import { saveStateToIndexedDB } from './storeDB';
import { debounce } from 'lodash-es';

export interface RootState {
  mapStyles: BaseMapStylesState,
  planTrail: PlanTrailState
  waypoints: WaypointDialogState
  mapDataOverlays: BaseMapDataOverlaysState
  mapDataPOIs: BaseMapDataPOIsState
}

const store: Store<RootState> = createStore({
  modules: {
    mapStyles: BaseMapStylesDialogModule,
    planTrail: PlanTrailModule,
    waypoints: WaypointDialogModule,
    mapDataOverlays: BaseMapDataOverlaysModule,
    mapDataPOIs: BaseMapDataPOIsModule,
  }
})

// Mutations that should trigger immediate save (critical operations)
const immediateSaveMutations = [
  'planTrail/createNewRoute',
  'planTrail/removeRoute',
  'planTrail/updateRouteName',
  'planTrail/setActiveRoute',
  'planTrail/addHikeHighlight',
  'planTrail/removeHikeHighlight',
  'planTrail/updateHikeHighlight',
  'planTrail/setHikeHighlights',
  'planTrail/setSelectedFeatureTags',
  'planTrail/setSelectedActivityTags'
];

// Mutations that can be debounced (frequent edits like dragging)
const debouncedSaveMutations = [
  'planTrail/addLineString',
  'planTrail/addLineStrings',
  'planTrail/addLineStringAtIndex',
  'planTrail/addToRedoMarkers',
  'planTrail/addToRedoLineStrings',
  'planTrail/syncActiveRouteFromLegacy',
  'planTrail/syncLegacyFromActiveRoute',
  'planTrail/setMarkers',
  'planTrail/addMarker',
  'planTrail/removeMarker',
  'planTrail/updateMarker',
  'planTrail/insertMarkerObj',
  'planTrail/removeLineStringAtIndex',
  'planTrail/setLineStrings',
  'planTrail/updateUndo',
  'planTrail/clearRedo',
  'planTrail/validateAndCleanAllRoutes'
];

// Combined list for reference
const saveableMutations = [...immediateSaveMutations, ...debouncedSaveMutations];

// Optimized state preparation function
function prepareStateForSave(state: RootState) {
  // Strip non-serializable fields before cloning.
  // We must do this on the live state briefly, so wrap in try/finally to guarantee restore.
  const pt = (state as any).planTrail;
  const savedUndoManager = pt?.undoManager;
  const savedHoverCache = pt?._hoverDistanceCache;
  try {
    if (pt) {
      pt.undoManager = null;
      pt._hoverDistanceCache = null;
    }
    var stateCopy = JSON.parse(JSON.stringify(state));
  } finally {
    // Always restore, even if JSON.stringify throws
    if (pt) {
      pt.undoManager = savedUndoManager;
      pt._hoverDistanceCache = savedHoverCache;
    }
  }

  stateCopy.mapStyles = new BaseMapStylesState();

  // Clear undo/redo stacks from all routes in a single pass
  if (stateCopy.planTrail.routes) {
    for (const routeKey in stateCopy.planTrail.routes) {
      const route = stateCopy.planTrail.routes[routeKey];
      route.undoMarkers = [];
      route.redoMarkers = [];
      route.undoLineStrings = [];
      route.redoLineStrings = [];
    }
  }

  // Clear legacy undo/redo
  stateCopy.planTrail.redoLineStrings = [];
  stateCopy.planTrail.redoMarkers = [];
  stateCopy.planTrail.undoLineStrings = [];
  stateCopy.planTrail.undoMarkers = [];

  // Clear transient selection state — never persist across page reloads
  stateCopy.planTrail.isAreaSelecting = false;
  stateCopy.planTrail.areaSelectedMarkers = [];

  // Clear image preview URLs from highlights in a single pass
  if (stateCopy.planTrail.hikeHighlights) {
    for (const highlight of stateCopy.planTrail.hikeHighlights) {
      if (highlight?.imagePreviewUrls) {
        delete highlight.imagePreviewUrls;
      }
    }
  }

  return stateCopy;
}

// Immediate save function for critical mutations
function saveImmediately(state: RootState) {
  try {
    const stateCopy = prepareStateForSave(state);
    saveStateToIndexedDB(stateCopy).catch(error => {
      console.error('Failed to save state to IndexedDB:', error);
    });
  } catch (error) {
    console.error('Failed to prepare state for save:', error);
  }
}

// Debounced save function - waits 1 second after the last mutation before saving
// This prevents excessive saves during rapid edits (e.g., dragging a marker)
const debouncedSave = debounce((state: RootState) => {
  saveImmediately(state);
}, 1000);

store.subscribe((mutation, state) => {
  if (immediateSaveMutations.includes(mutation.type)) {
    // Critical mutations: flush any pending debounced save and save immediately
    debouncedSave.flush();
    saveImmediately(state);
  } else if (debouncedSaveMutations.includes(mutation.type)) {
    // Non-critical mutations: use debounced save to avoid blocking UI
    debouncedSave(state);
  }
})

// Flush any pending saves before page unload to prevent data loss
window.addEventListener('beforeunload', () => {
  // Flush will execute any pending debounced save immediately
  debouncedSave.flush();
});



export default store;
